import type { components } from '@/api'
import type { ImportResponse } from '@/backend/startImport'
import type {
  CaseResponse,
  EntityResponse,
  FieldResponse,
  FolderResponse,
  ProjectResponseInList,
  PropertyResponse,
  SpaceResponse,
  SpaceResponseMessage,
  TriggerResponse,
} from '@/backend/types'
import type { UpdateViewResponse } from '@/backend/updateView'
import type { MessageResponse, PaginatedCaseResponse } from '@/modules/Cases/types'
import { toValue, type MaybeRefOrGetter } from 'vue'
type s = components['schemas']

type SocketProjectResponse = ProjectResponseInList

// Events for ask_go topic
export type AskgoEvents = {
  'ask_go:new_message': s['AskGo.SessionResponse']
}

// Events for case topic
export type CaseEvents = {
  'case:updated': { case: CaseResponse }
  'case:deleted': { case_id: string }
  'case:message_stream': { message: MessageResponse }
}

// Events for integrations topic
export type IntegrationsEvents = {
  'connection:confirmed': { connection_id: string; user_id: string; integration_id: string }
  'connection:deleted': { connection_id: string; integration_id: string }
}

// Events for project topic
export type ProjectEvents = {
  'entities:stale': Record<string, never>
  'project:updated': SocketProjectResponse
  'property:updated': PropertyResponse
  'property:deleted': { property_id: string }
  'entity:bulk_deleted': { entity_ids: string[] }
  'view:updated': UpdateViewResponse
  'view:deleted': { id: string }
  'import:started': ImportResponse
  'import:processed': ImportResponse
  'entity:created': EntityResponse
  'entity:updated': EntityResponse
  'entity:deleted': { entity_id: string }
  'entity:active_view_removed': { entity_id: string; view_id: string }
  'field:updated': FieldResponse
}

// Events for spaces topic
export type SpacesEvents = {
  'spaces:messages_updated': {
    space_id: string
    space_name: string
    status: SpaceResponse['status']
    timestamp: string
    messages: SpaceResponseMessage[]
  }
}

// Events for workspace topic
export type WorkspaceEvents = {
  /** ⚠️ project:created doesn't get triggered when a project is created  */
  'project:created': SocketProjectResponse
  'project:deleted': { id: string }
  'project:updated': SocketProjectResponse
  /** ⚠️ folder:created doesn't get triggered when a folder is created  */
  'folder:created': FolderResponse
  'folder:updated': FolderResponse
  'folder:deleted': { id: string }
  'trigger:created': TriggerResponse
  'trigger:updated': TriggerResponse
  'trigger:deleted': { id: string }
  'import:started': ImportResponse
  'import:processed': ImportResponse
  'case:created': { case: PaginatedCaseResponse }
  'case:updated': { case: PaginatedCaseResponse }
  'case:deleted': { case_id: string }
}

// All available topics
export type AskGoTopic = `ask_go:${string}`
export type CaseTopic = `case:${string}:${string}`
export type IntegrationsTopic = `integrations:${string}`
export type ProjectTopic = `project:${string}`
export type SpacesTopic = `spaces:${string}`
export type WorkspaceTopic = `workspace:${string}`

export type WebSocketTopic =
  | AskGoTopic
  | CaseTopic
  | IntegrationsTopic
  | ProjectTopic
  | SpacesTopic
  | WorkspaceTopic

// Main event map for type-safe subscriptions
export type WebSocketEventMap = {
  ask_go: AskgoEvents
  case: CaseEvents
  integrations: IntegrationsEvents
  project: ProjectEvents
  spaces: SpacesEvents
  workspace: WorkspaceEvents
}

// Helper type to extract topic prefix (everything before the first colon)
type ExtractTopicPrefix<T> = T extends `${infer Prefix}:${string}` ? Prefix : never

// Helper types for extracting event names and payloads
export type EventsForTopic<T extends WebSocketTopic> = WebSocketEventMap[ExtractTopicPrefix<T>]

export type EventNamesForTopic<T extends WebSocketTopic> = keyof EventsForTopic<T>

export type PayloadForEvent<
  T extends WebSocketTopic,
  E extends EventNamesForTopic<T>,
> = EventsForTopic<T>[E]

type TopicNameParam = MaybeRefOrGetter<string | null | undefined>

export const wsTopic = {
  askGo(sessionId: TopicNameParam): AskGoTopic | null {
    const sessionValue = toValue(sessionId)
    return sessionValue ? `ask_go:${sessionValue}` : null
  },
  case(workspaceId: TopicNameParam, caseId: TopicNameParam): CaseTopic | null {
    const workspaceValue = toValue(workspaceId)
    const caseValue = toValue(caseId)
    if (!workspaceValue || !caseValue) return null
    return `case:${workspaceValue}:${caseValue}`
  },
  integrations(integrationId: TopicNameParam): IntegrationsTopic | null {
    const integrationValue = toValue(integrationId)
    return integrationValue ? `integrations:${integrationValue}` : null
  },
  project(projectId: TopicNameParam): ProjectTopic | null {
    const projectValue = toValue(projectId)
    return projectValue ? `project:${projectValue}` : null
  },
  spaces(spaceId: TopicNameParam): SpacesTopic | null {
    const spaceValue = toValue(spaceId)
    return spaceValue ? `spaces:${spaceValue}` : null
  },
  workspace(workspaceId: TopicNameParam): WorkspaceTopic | null {
    const workspaceValue = toValue(workspaceId)
    return workspaceValue ? `workspace:${workspaceValue}` : null
  },
} as const
